/* eslint-disable @angular-eslint/no-input-rename */
import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Directive, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { Position, TooltipComponent, TooltipContent, TooltipMaxWidth, TooltipStyle } from '../components';

export interface TooltipDirectiveConfig {
  position?: Position;
  style?: TooltipStyle;
  maxWidth?: TooltipMaxWidth;
  openDelay?: NumberInput;
  closeDelay?: NumberInput;
}

@Directive({ selector: '[vsTooltip]', exportAs: 'vsTooltip', standalone: false })
export class TooltipDirective implements TooltipDirectiveConfig, OnDestroy {
  @Input('vsTooltip') content?: TooltipContent;

  @Input('tooltipConfig') set config(config: TooltipDirectiveConfig | undefined | null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries(config ?? {}).forEach(([key, value]) => ((this as any)[key] = value));
  }

  @Input('tooltipPosition') position: Position = 'top';
  @Input('tooltipStyle') style: TooltipStyle = 'dark';
  @Input('tooltipMaxWidth') maxWidth: TooltipMaxWidth = 'lg';

  private _openDelay = 0;
  @Input('tooltipOpenDelay') set openDelay(value: NumberInput) {
    this._openDelay = coerceNumberProperty(value);
  }

  private _closeDelay = 0;
  @Input('tooltipCloseDelay') set closeDelay(value: NumberInput) {
    this._closeDelay = coerceNumberProperty(value);
  }

  @Input('tooltipAnchor') anchor?: ElementRef<unknown>;

  private overlayRef?: OverlayRef = this.overlay.create();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private openTimeout?: any;
  private closing = false;
  private destroy$ = new Subject<void>();

  constructor(
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private overlay: Overlay,
    router: Router
  ) {
    router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.dispose());
  }

  @HostListener('mouseenter')
  protected _mouseenter() {
    this.showTooltip();
  }

  @HostListener('click')
  protected _click() {
    if (this.overlayRef?.hasAttached()) {
      this.hideTooltip(true);
      return;
    }

    this.showTooltip();
  }

  @HostListener('mouseleave')
  protected _mouseleave() {
    this.hideTooltip();
  }

  private showTooltip() {
    if (!this.content) return;
    this.closing = false;

    if (this.openTimeout) clearTimeout(this.openTimeout);
    this.openTimeout = undefined;

    if (this.overlayRef === undefined) this.overlayRef = this.overlay.create();
    if (this.overlayRef?.hasAttached()) this.overlayRef?.detach();

    const positionConfigs = this.getPositionConfigs();
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.anchor ?? this.elementRef)
      .withViewportMargin(OFFSET_SIZE)
      .withPositions(positionConfigs);

    this.overlayRef?.updatePositionStrategy(positionStrategy);

    const open = () => {
      const tooltipRef = this.overlayRef?.attach(new ComponentPortal(TooltipComponent));
      if (tooltipRef) {
        tooltipRef.instance.originElementRef = this.anchor ?? this.elementRef;
        tooltipRef.instance.content = this.content;
        tooltipRef.instance.style = this.style;
        tooltipRef.instance.maxWidth = this.maxWidth;
      }
    };

    if (this._openDelay) this.openTimeout = setTimeout(() => open(), this._openDelay);
    else open();
  }

  private hideTooltip(force = false) {
    if (this.openTimeout) clearTimeout(this.openTimeout);
    this.openTimeout = undefined;

    if (force || !this._closeDelay) {
      this.overlayRef?.detach();
      this.closing = false;
      return;
    }

    this.closing = true;
    setTimeout(() => {
      if (this.closing) {
        this.overlayRef?.detach();
        this.closing = false;
      }
    }, this._closeDelay);
  }

  private getPositionConfigs() {
    switch (this.position) {
      case 'top':
        return [TOP_POSITION, BOTTOM_POSITION];
      case 'bottom':
        return [BOTTOM_POSITION, TOP_POSITION];
      case 'start':
        return [START_POSITION, END_POSITION];
      case 'end':
        return [END_POSITION, START_POSITION];
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.dispose();
  }

  private dispose() {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }
}

const OFFSET_SIZE = 12;

const TOP_POSITION: ConnectedPosition = {
  originX: 'center',
  originY: 'top',
  overlayX: 'center',
  overlayY: 'bottom',
  offsetX: 0,
  offsetY: -OFFSET_SIZE,
};

const BOTTOM_POSITION: ConnectedPosition = {
  originX: 'center',
  originY: 'bottom',
  overlayX: 'center',
  overlayY: 'top',
  offsetX: 0,
  offsetY: OFFSET_SIZE,
};

const START_POSITION: ConnectedPosition = {
  originX: 'start',
  originY: 'center',
  overlayX: 'end',
  overlayY: 'center',
  offsetX: -OFFSET_SIZE * 2,
  offsetY: 0,
};

const END_POSITION: ConnectedPosition = {
  originX: 'end',
  originY: 'center',
  overlayX: 'start',
  overlayY: 'center',
  offsetX: OFFSET_SIZE,
  offsetY: 0,
};
